/** @jsxImportSource theme-ui */

import React, { useState, ChangeEvent } from 'react';
import { ExtractableInputWrapperProps } from '../utils/InputWrapper';
import InputWrapper, { extractWrapperProps } from '../utils/InputWrapper';
import { getVariantOrBase } from '../utils';
import { ErrorProps } from '../utils/types/error';
import { StyleOverrideProps } from '../utils/types';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';
import { DEFAULT_TEXTAREA_CHAR_MAX_LENGTH, defaultShouldDisplayCount } from '../utils/DefaultCountBehavior';

/* Account for top and bottom padding */
const PADDING_SIZE = 16 * 2;

export type TextareaVariants = 'error';
export interface NakedTextareaProps
    extends StyleOverrideProps,
        ErrorProps,
        Pick<TextareaAutosizeProps, 'minRows' | 'maxRows'>,
        Omit<React.ComponentPropsWithRef<'textarea'>, 'css' | 'style' | 'rows'> {
    variant?: TextareaVariants;
    autosize?: boolean;
}
export interface TextareaProps
    extends ExtractableInputWrapperProps,
        NakedTextareaProps,
        Omit<NakedTextareaProps, 'variant'> {
    shouldDisplayCount?: boolean | ((count: number, maxLength: number) => boolean);
}

export const NakedTextarea = (props: NakedTextareaProps): JSX.Element => {
    const { sx, isError, autosize, maxRows, minRows, maxLength, ...otherProps } = props;
    const variant = isError ? 'error' : props.variant;
    const [overflowStyle, setOverflowStyle] = React.useState(autosize ? 'hidden' : 'inherit');
    return (
        <TextareaAutosize
            minRows={minRows}
            maxRows={autosize ? maxRows : minRows}
            maxLength={maxLength}
            onHeightChange={(height, { rowHeight }) => {
                if (autosize) {
                    const numberOfRows = (height - PADDING_SIZE) / rowHeight;
                    setOverflowStyle(numberOfRows < maxRows ? 'hidden' : 'inherit');
                }
            }}
            sx={{
                ...sx,
                variant: getVariantOrBase('forms.textarea', variant),
                resize: 'none',
                overflow: overflowStyle,
            }}
            {...otherProps}
        />
    );
};

export const Textarea = (props: TextareaProps): JSX.Element => {
    const { count, isError, shouldDisplayCount, onChange, maxLength, ...restProps } = props;

    const [currentCount, setCurrentCount] = useState(count);

    const onChangeWithCountState = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setCurrentCount(e.target.value.length);
        if (onChange) {
            onChange(e);
        }
    };

    const { extractedProps, otherProps: baseTextareaProps } = extractWrapperProps<TextareaProps>(restProps);

    const wrapperProps = {
        displayCount:
            typeof shouldDisplayCount === 'function' ? shouldDisplayCount(currentCount, maxLength) : shouldDisplayCount,
        ...extractedProps,
        count: currentCount,
        isError,
        maxLength,
    };

    const textareaProps = {
        ...baseTextareaProps,
        isError,
        onChange: onChangeWithCountState,
        maxLength,
    };

    return (
        <InputWrapper {...wrapperProps}>
            <NakedTextarea {...textareaProps} />
        </InputWrapper>
    );
};
Textarea.defaultProps = {
    shouldDisplayCount: defaultShouldDisplayCount,
    count: 0,
    maxLength: DEFAULT_TEXTAREA_CHAR_MAX_LENGTH,
    autosize: false,
    minRows: 2,
    maxRows: Infinity,
};
